"use client";

import React from "react";
import { Activity, ArrowRight, Brain, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const advantagesList = [{
  icon: Activity,
  title: "Desafios Comuns na Produção de Vinho",
  description: "Os desafios comuns incluem:\n\nTempo de rotação lento dos tanques entre lotes.\nResíduos de vinho, acumulação de tártaro e borras que permanecem após a limpeza.\nProcessos de limpeza manuais que exigem entrada laboriosa nos tanques.\nLimpeza inconsistente de barris, contentores, recipientes e zonas de difícil acesso.\nConsumo excessivo de água, produtos químicos ou energia durante a limpeza e lavagem.\nGarrafas húmidas que interferem com a rotulagem, codificação ou embalagem.\nPreocupações de segurança relacionadas com mangueiras, pavimentos escorregadios e lavagem manual.",
  isPrimary: false
}, {
  icon: Brain,
  title: "As Nossas Soluções para Adegas",
  description: "Com as nossas soluções orientadas para adegas, pode:\n\nLimpar de forma mais completa tanques de fermentação, tanques de armazenamento e recipientes de pequenas dimensões.\nReduzir a limpeza manual e limitar a entrada em espaços confinados.\nMelhorar o impacto de limpeza em resíduos persistentes, borras e acumulação de tártaro.\nLavar de forma eficiente pavimentos, equipamentos, prensas, transportadores e zonas de enchimento.\nMelhorar a secagem antes da rotulagem, codificação e embalagem secundária.\nReduzir o consumo de ar comprimido, água e produtos químicos.\nMelhorar a repetibilidade nos processos de limpeza e sanitização.",
  isPrimary: false
}, {
  icon: ShieldCheck,
  title: "Importância de um Desempenho de Pulverização Consistente na Vinificação",
  description: "A vinificação exige um controlo rigoroso em todas as etapas da produção. Os resíduos que permanecem em tanques, barris, linhas de transferência ou em equipamentos de produção podem afetar a qualidade do produto, a sanitização e a produtividade. Ao mesmo tempo, as adegas necessitam de formas práticas de reduzir o tempo de limpeza, conservar água e produtos químicos, e apoiar condições de trabalho seguras para as equipas de adega e de produção.\nA Spraying Systems Co. ajuda as adegas a otimizar operações críticas de pulverização em toda a instalação, incluindo limpeza de tanques, lavagem de barris e equipamentos, limpeza de adegas e secagem e sopro de garrafas.",
  isPrimary: true
}];
export default function Coating() {
  return <section id="winetechnicaladvantages" className="relative bg-background text-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      {}
      <div className="aria-hidden:hidden pointer-events-none absolute -top-40 -left-40 size-[500px] rounded-lg bg-secondary/10 blur-3xl" />
      <div className="aria-hidden:hidden pointer-events-none absolute bottom-0 right-0 size-[600px] rounded-lg bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {}
          <div className="lg:col-span-5 lg:sticky lg:top-28 flex flex-col items-start gap-6">
            <span className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
              EUROCONTROL
            </span>

            <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">Tecnologia de Pulverização para a Indústria do Vinho</h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">A Spraying Systems Co. disponibiliza tecnologia de pulverização e expertise de aplicação para adegas, vinhas e unidades de produção de vinho. Desde tanques de fermentação e barris até linhas de engarrafamento e lavagem de adegas, as nossas soluções ajudam as adegas a melhorar a consistência da limpeza, reduzir o consumo de água e de produtos químicos, proteger a qualidade do produto e manter a produção em movimento.</p>

            <div className="pt-2">
              <Button variant="ghost" className="p-0 h-auto hover:bg-transparent font-semibold group text-foreground hover:text-secondary" asChild>
                <Link to="/TechnicalSolutionsPage">
                  <span className="text-base">Conheça as soluções</span>
                  <ArrowRight className="size-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 flex flex-col gap-6 sm:gap-8">
            {advantagesList.map((item, i) => {
            const IconComponent = item.icon;
            return <motion.div key={i} data-index={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: "-50px"
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className={`rounded-2xl p-6 sm:p-8 border transition-all duration-300 ${item.isPrimary ? "bg-primary text-primary-foreground border-primary shadow-lg" : "bg-card/90 backdrop-blur-md text-card-foreground border-border/80 shadow-sm hover:shadow-md"}`}>
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className={`size-16 rounded-full flex items-center justify-center shrink-0 ${item.isPrimary ? "bg-primary-foreground/15 text-primary-foreground" : "bg-muted text-primary"}`}>
                      <IconComponent className="size-8 stroke-[1.75]" />
                    </div>

                    <div className="flex flex-col gap-3">
                      <h3 className={`font-default text-xl sm:text-2xl font-semibold tracking-tight ${item.isPrimary ? "text-primary-foreground" : "text-foreground"}`}>
                        {item.title}
                      </h3>
                      <p className={`text-sm sm:text-base leading-relaxed ${item.isPrimary ? "text-primary-foreground/90" : "text-muted-foreground"}`}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}